import React, { useMemo, useState } from "react";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { MONTHS_IT } from "@/lib/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SuiteButton } from "./SuiteButton";

function shiftedPeriod(year, month, offset) {
  const date = new Date(Number(year), Number(month) - 1 + offset, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

/**
 * Selettore mese/anno unico della Suite.
 *
 * È il solo controllo da usare nelle pagine operative mensili: frecce,
 * pulsante periodo, preset e griglia mesi condividono così grafica,
 * accessibilità e comportamento di chiusura.
 */
export function SuiteMonthPicker({
  year,
  month,
  onChange,
  size = "md",
  align = "center",
  className = "",
  testid = "suite-month-picker",
  showNavigation = true,
}) {
  const [open, setOpen] = useState(false);
  const current = useMemo(() => new Date(), []);
  const currentYear = current.getFullYear();
  const currentMonth = current.getMonth() + 1;
  const years = useMemo(() => {
    const start = Math.min(Number(year) - 3, currentYear - 2);
    const end = Math.max(Number(year) + 3, currentYear + 2);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [currentYear, year]);

  const apply = (nextYear, nextMonth, close = true) => {
    onChange?.({ year: Number(nextYear), month: Number(nextMonth) });
    if (close) setOpen(false);
  };

  const goPrevious = () => {
    const previous = shiftedPeriod(year, month, -1);
    apply(previous.year, previous.month, false);
  };

  const goNext = () => {
    const next = shiftedPeriod(year, month, 1);
    apply(next.year, next.month, false);
  };

  const compact = size === "sm";
  const navClass = compact ? "!h-8 !w-8 !px-0" : "!h-9 !w-9 !px-0";
  const triggerClass = compact
    ? "!h-8 min-w-[150px] !px-3 text-sm"
    : "!h-9 min-w-[176px] !px-4 text-base";

  const quickPresets = [
    { label: "Oggi", year: currentYear, month: currentMonth },
    { label: "Questo mese", year: currentYear, month: currentMonth },
    { label: "Mese scorso", ...shiftedPeriod(currentYear, currentMonth, -1) },
    { label: "Questo anno", year: currentYear, month },
    { label: "Anno scorso", year: currentYear - 1, month },
  ];

  return (
    <div
      className={`inline-flex items-center rounded-lg border border-suite-primary/25 bg-suite-primary-light/35 p-1 shadow-sm ${className}`}
      data-testid={`${testid}-navigation`}
    >
      {showNavigation && (
        <SuiteButton
          variant="ghost"
          size={compact ? "sm" : "md"}
          className={navClass}
          onClick={goPrevious}
          title="Mese precedente"
          aria-label="Mese precedente"
          data-testid={`${testid}-previous`}
        >
          <ChevronLeft size={compact ? 15 : 18} />
        </SuiteButton>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <SuiteButton
            variant="secondary"
            size={compact ? "sm" : "md"}
            className={`${triggerClass} border-0 font-bold text-suite-primary shadow-none`}
            data-testid={`${testid}-trigger`}
          >
            <CalendarDays size={compact ? 15 : 17} />
            {MONTHS_IT[Number(month) - 1]} {year}
            <ChevronDown size={compact ? 14 : 15} />
          </SuiteButton>
        </PopoverTrigger>

        <PopoverContent align={align} className="w-[310px] p-3.5 space-y-3" data-testid={`${testid}-panel`}>
          <section>
            <div className="mb-2 text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500">
              Preset rapidi
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {quickPresets.map((preset, index) => {
                const selected = Number(year) === Number(preset.year) && Number(month) === Number(preset.month);
                return (
                  <SuiteButton
                    key={`${preset.label}-${index}`}
                    variant={selected ? "outline" : "secondary"}
                    size="sm"
                    className="w-full !px-1 font-medium"
                    onClick={() => apply(preset.year, preset.month)}
                    data-testid={`${testid}-preset-${index}`}
                  >
                    {preset.label}
                  </SuiteButton>
                );
              })}
            </div>
          </section>

          <section className="border-t border-suite-neutral-200 pt-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500">
                Seleziona mese
              </div>
              <Select value={String(year)} onValueChange={(value) => apply(Number(value), month, false)}>
                <SelectTrigger className="h-8 w-24 text-xs" data-testid={`${testid}-year`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((item) => (
                    <SelectItem key={item} value={String(item)}>{item}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 gap-1.5">
              {MONTHS_IT.map((label, index) => {
                const value = index + 1;
                const selected = Number(month) === value;
                return (
                  <SuiteButton
                    key={label}
                    variant={selected ? "outline" : "secondary"}
                    size="sm"
                    className="w-full !px-1 font-medium"
                    onClick={() => apply(year, value)}
                    data-testid={`${testid}-month-${value}`}
                  >
                    {label.slice(0, 3)}
                  </SuiteButton>
                );
              })}
            </div>
          </section>
        </PopoverContent>
      </Popover>

      {showNavigation && (
        <SuiteButton
          variant="ghost"
          size={compact ? "sm" : "md"}
          className={navClass}
          onClick={goNext}
          title="Mese successivo"
          aria-label="Mese successivo"
          data-testid={`${testid}-next`}
        >
          <ChevronRight size={compact ? 15 : 18} />
        </SuiteButton>
      )}
    </div>
  );
}

export default SuiteMonthPicker;
